/* eslint-disable no-unused-vars */
import { MenuItem } from '@mui/material';
import { YEAR_DURATIONS } from './constants/contactApp';
import { ColorsV2 } from 'app/constants/color';
import { current } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import moment from 'moment';
import { LINE_CHART_COLORS_V2 } from './constants';
import { persistor, store } from 'app/store/store';
import { addAlert } from 'app/store/features/alertSlice';
import apiService from 'app/services/apiService';
import { setUser } from 'app/store/features/updateUserSlice';

export const timeSince = days => {
  const calcDate = new Date(Date.now() - days * 24 * 3600 * 1000);
  const seconds = Math.floor((new Date() - calcDate) / 1000);

  let interval = seconds / 31536000;

  if (interval > 1) {
    return Math.floor(interval) + 'y ago';
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + 'm ago';
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + 'd ago';
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + 'h ago';
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + 'm ago';
  }
  return Math.floor(seconds) + 's ago';
};

export const getColorObject = color => {
  if (typeof color === 'string') {
    return {
      type: 'color',
      value: color,
    };
  }

  if (color.length === 1) {
    return {
      type: 'color',
      value: color,
    };
  }

  return {
    type: 'gradient',
    value: color,
  };
};

export const getBgColorStyle = (colors, gradientDir) => {
  const colorObject = getColorObject(colors);
  if (!colorObject) return null;

  if (colorObject.type === 'color') {
    return { backgroundColor: colorObject.value };
  } else if (colorObject.type === 'gradient') {
    if (gradientDir)
      return { backgroundImage: `linear-gradient(${gradientDir}, ${colorObject.value.join()})` };
    return { backgroundImage: `linear-gradient(${colorObject.value.join()})` };
  }
};

export const getBgImageStyle = imgSrc => {
  return {
    background: `url(${imgSrc}) no-repeat center`,
    backgroundSize: 'cover',
  };
};

export const colorForBgColor = hexColor => {
  hexColor = hexColor.replace('#', '');
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);

  return r < 100 || g < 100 || b < 100 ? 'white' : '#333333';
  // const yiq = ((r*299)+(g*587)+(b*114))/1000;
  // return (yiq >= 128) ? 'black' : 'white';
};

export const getMarginStyle = input => {
  let margin = {};
  if (typeof input === 'number' || typeof input === 'string') {
    margin = typeof input === 'number' ? `${input}px` : input;
    return { top: margin, bottom: margin, left: margin, right: margin };
  } else if (Array.isArray(input)) {
    if (input.length === 1) {
      margin = typeof input[0] === 'number' ? `${input[0]}px` : input[0];
      return { top: margin, bottom: margin, left: margin, right: margin };
    } else if (input.length === 2) {
      const marginX = typeof input[0] === 'number' ? `${input[0]}px` : input[0];
      const marginY = typeof input[1] === 'number' ? `${input[1]}px` : input[1];
      return { top: marginY, bottom: marginY, left: marginX, right: marginX };
    } else if (input.length === 3 || input.length === 4) {
      const marginTop = typeof input[0] === 'number' ? `${input[0]}px` : input[0];
      const marginRight = typeof input[1] === 'number' ? `${input[1]}px` : input[1];
      const marginBottom = typeof input[2] === 'number' ? `${input[2]}px` : input[2];
      if (input.length === 3) {
        return { top: marginTop, bottom: marginBottom, left: 0, right: marginRight };
      } else {
        const marginLeft = typeof input[3] === 'number' ? `${input[3]}px` : input[3];
        return { top: marginTop, bottom: marginBottom, left: marginLeft, right: marginRight };
      }
    }
  }
  return {};
};

export const parseGradientArray = gradientArray => {
  const gradients = [];
  let percentageDefined = 0;
  let undefinedGradientCounts = 0;

  for (let count = 0; count < gradientArray.length; count++) {
    const gradientValues = gradientArray[count].trim().split(' ');

    if (gradientValues.length === 1) {
      gradients.push({ color: gradientValues[0], percent: -1 });
      undefinedGradientCounts++;
    } else {
      // eslint-disable-next-line radix
      const percent = parseInt(gradientValues[gradientValues.length - 1].trim());
      gradients.push({ color: gradientValues[0], percent });
      percentageDefined += percent;
    }
  }

  if (undefinedGradientCounts) {
    const equalPercent = (100 - percentageDefined) / undefinedGradientCounts;
    for (let count = 0; count < gradients.length; count++) {
      if (gradients[count].percent === -1) {
        gradients[count].percent = equalPercent;
      }
    }
  }
  return gradients;
};

export const getDateElements = date => {
  const dateString = moment(date).format('dddd, MMMM DD YYYY, hh:mm A');
  const dateSections = dateString.split(',');
  const day = dateSections[0];
  const time = dateSections[2];
  const datePart = dateSections[1].trim().split(' ');
  return {
    day,
    time,
    date: {
      dateString: dateSections[1],
      month: datePart[0],
      date: datePart[1],
      year: datePart[2],
    },
  };
};

export const getCustomDateTime = (value = 0, unit = 'days', format = 'HH:mm a | MMMM DD, YYYY') => {
  if (value === 0) {
    return moment().format(format);
  } else {
    return moment().add(value, unit).format(format);
  }
};
export const getCustomDate = (value = 0, unit = 'days', format = 'hh:mm , DD MMMM') => {
  if (value === 0) {
    return 'Today';
  }
  if (value === 1) {
    return 'Yesterday';
  } else {
    return moment().subtract(value, unit).format(format);
  }
};
export const getCustomTime = (value = 0, unit = 'minutes', format = 'hh:mm A') => {
  if (value === 0) {
    return moment().format(format);
  } else {
    return moment().subtract(value, unit).format(format);
  }
};
export const getYesterdayDate = () => {
  return moment().subtract(1, 'day').format('DD.MM.YYYY');
};
export const capitalizeFirstLetter = inputString => {
  if (!inputString) return;
  return inputString.charAt(0).toUpperCase() + inputString.toLowerCase().slice(1);
};

export const camelToSpaceCase = input => {
  if (!input) {
    return '';
  }
  return input.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase());
};

export const isValidEmail = emailAddress => {
  const pattern = new RegExp(
    /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i
  );
  return pattern.test(emailAddress);
};

export function customMap(arr, index = 0) {
  if (index === arr.length) {
    return 0;
  }

  if (index === 0) {
    return 1;
  }

  return arr[index - 1].length + customMap(arr, index - 1);
}

export const parseAndFormatDate = (dateString, format = 'DD MMM YYYY') => {
  if (!dateString) {
    return 'N/A';
  }
  const formattedDate = moment(dateString).format(format);
  return formattedDate;
};

export const formatTwoDigits = number => {
  return number < 10 ? `0${number}` : number?.toString();
};

export const formatNumber = (value, decimal = 2) => {
  if (!value) return 0;
  if (value >= 1000000) {
    return (value / 1000000).toFixed(decimal) + 'M';
  } else if (value >= 1000) {
    return (value / 1000).toFixed(decimal) + 'K';
  } else {
    return value.toString();
  }
};

export const ASSIGNED_CHART_COLORS = {
  undefined: ColorsV2.white,
  '': ColorsV2.white,
};

// Checks if the label provided has already been assigned a color and returns it
// If no color has been assigned previously then it assigns it a color from the LINE_CHART_COLORS_V2 array
// This function will automatically loop through the color array when all colors have been used.
// NOTE: This function will keep looping regardless of where the colors where initally assigned from, therefore color hierarchy will not be respected across different graphs

// NOTE: This function could also be refactored to be a proper custom hook e.g useChartColorSelect
export const checkForAssignedChartColor = label => {
  const assignedColors = Object.keys(ASSIGNED_CHART_COLORS).length;
  const nextColor = LINE_CHART_COLORS_V2[assignedColors % LINE_CHART_COLORS_V2.length];
  if (ASSIGNED_CHART_COLORS[label]) {
    return ASSIGNED_CHART_COLORS[label];
  } else {
    ASSIGNED_CHART_COLORS[label] = nextColor;
    return ASSIGNED_CHART_COLORS[label];
  }
};

export const arbitraryLinePlugin = (formattedData, setFetchedMultipleMilestoneData, version) => {
  return {
    listeners: [], // keep track of event listeners

    beforeDatasetsDraw(chart) {
      // remove existing event listeners
      this.listeners.forEach(listener => {
        chart.canvas.removeEventListener(listener.type, listener.handler);
      });
      this.listeners = [];

      const {
        ctx,
        chartArea: { top, bottom, left, right },
        scales: { x: xAxis },
      } = chart;

      formattedData?.forEach(({ description, date, pointY, name, id }) => {
        const lineDate = new Date(date);
        const lineX = xAxis.getPixelForValue(lineDate);
        const xAxisMin = xAxis.getValueForPixel(chart.chartArea.left);
        const xAxisMax = xAxis.getValueForPixel(chart.chartArea.right);

        if (lineDate < xAxisMin || lineDate > xAxisMax) {
          return; // Skip drawing the line if it's outside the visible range
        } else {
          ctx.save();
          ctx.beginPath();
          ctx.lineWidth = 3;
          ctx.strokeStyle = 'lightGrey';
          ctx.setLineDash([5, 5]);
          ctx.moveTo(lineX, top);
          ctx.lineTo(lineX, bottom);
          ctx.stroke();
          ctx.restore();

          const lineCount = formattedData?.filter(item => item.date === date).length;
          // draw circular point
          ctx.save();
          ctx.beginPath();
          const pointRadius = 7;
          const pointX = lineX;
          ctx.arc(pointX, pointY, pointRadius + 5, 0, 2 * Math.PI);
          ctx.fillStyle = ColorsV2.acent;
          ctx.fill();
          ctx.strokeStyle = ColorsV2.acent;
          ctx.lineWidth = 0;
          ctx.fillStyle = ColorsV2.white;
          ctx.font = 'bold 12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          if (lineCount > 1) {
            ctx.fillText(lineCount.toString(), pointX, pointY);
          }

          // ctx.restore();
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(pointX, pointY, pointRadius + 4, 0, 2 * Math.PI);
          ctx.strokeStyle = ColorsV2.white;
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.restore();

          const tooltip = {
            visible: false,
            lastX: null,
            lastY: null,
            data: { name, date: parseAndFormatDate(lineDate, 'DD MMMM YYYY') },
          };

          // add mousemove event listener
          const mousemoveHandler = event => {
            const canvasRect = chart.canvas.getBoundingClientRect();
            const mouseX = event.clientX - canvasRect.left;
            const mouseY = event.clientY - canvasRect.top;
            const distance = Math.sqrt((mouseX - pointX) ** 2 + (mouseY - pointY) ** 2);

            if (distance < pointRadius) {
              if (!tooltip.visible || mouseX !== tooltip.lastX || mouseY !== tooltip.lastY) {
                this.clearTooltip(chart);
                tooltip.visible = true;
                tooltip.lastX = mouseX;
                tooltip.lastY = mouseY;
                this.drawTooltip(tooltip, chart, lineDate, xAxis, lineCount);
              }
            } else {
              if (tooltip.visible) {
                tooltip.visible = false;
                this.clearTooltip(chart);
              }
            }
          };
          chart.canvas.addEventListener('mousemove', mousemoveHandler);
          this.listeners.push({ type: 'mousemove', handler: mousemoveHandler });

          // Handle mouseout event to hide the tooltip
          const mouseoutHandler = () => {
            if (tooltip.visible) {
              tooltip.visible = false;
              this.clearTooltip(chart);
            }
          };
          chart.canvas.addEventListener('mouseout', mouseoutHandler);
          this.listeners.push({ type: 'mouseout', handler: mouseoutHandler });

          // add click event listener
          const clickHandler = event => {
            formattedData?.forEach(dataItem => {
              const date = dataItem.date;
              const pointY = dataItem.pointY;

              const xAxisMin = xAxis.getValueForPixel(chart.chartArea.left);
              const xAxisMax = xAxis.getValueForPixel(chart.chartArea.right);
              const lineDate = new Date(date);
              const lineX = xAxis.getPixelForValue(lineDate);
              const lineCount = formattedData?.filter(item => item.date === date).length;

              if (lineDate < xAxisMin || lineDate > xAxisMax) {
                return; // Skip handling the click if it's outside the visible range
              } else {
                const pointRadius = 5;
                const pointX = lineX;
                const canvasRect = ctx.canvas.getBoundingClientRect();
                const mouseX = event.clientX - canvasRect.left;
                const mouseY = event.clientY - canvasRect.top;
                const distance = Math.sqrt((mouseX - pointX) ** 2 + (mouseY - pointY) ** 2);
                if (distance < pointRadius + 5) {
                  setFetchedMultipleMilestoneData(prev => [
                    ...prev,
                    {
                      ...dataItem,
                    },
                  ]);
                }
              }
            });
          };
          chart.canvas.addEventListener('click', clickHandler);
          this.listeners.push({ type: 'click', handler: clickHandler });
        }
      });
    },

    drawTooltip(tooltip, chart, lineDate, xAxis, lineCount) {
      const { ctx } = chart;
      const tooltipPositioning = xAxis.getPixelForValue(lineDate);
      const tooltipX = tooltipPositioning >= 380 ? tooltip.lastX - 150 : tooltip.lastX + 10;
      const tooltipY = tooltip.lastY - 10;
      const tooltipWidth = 150;
      const tooltipHeight = 50;
      const tooltipPadding = 15;
      const tooltipMarginTop = 5;

      let tooltipText;
      if (lineCount > 1) {
        tooltipText = 'Multiple milestones exist, click to view';
      } else {
        tooltipText = `${tooltip.data.date}     ${tooltip.data.name}`; // space is to adjust the name to new line
      }

      const lines = [];
      const words = tooltipText.split(' ');
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = ctx.measureText(`${currentLine} ${word}`).width;
        if (width < tooltipWidth - tooltipPadding * 2) {
          currentLine += ` ${word}`;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);

      const lineHeight = 16;
      const tooltipContentHeight = tooltipPadding * 2 + lines.length * lineHeight;

      const tooltipRadius = 5;

      ctx.save();
      ctx.beginPath();
      ctx.fillStyle = version === 'v1' ? 'white' : '#E24BE6';
      ctx.strokeStyle = 'transparent';
      ctx.lineWidth = 3;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      ctx.shadowBlur = 5;
      ctx.moveTo(tooltipX + tooltipRadius, tooltipY);
      ctx.lineTo(tooltipX + tooltipWidth - tooltipRadius, tooltipY);
      ctx.quadraticCurveTo(
        tooltipX + tooltipWidth,
        tooltipY,
        tooltipX + tooltipWidth,
        tooltipY + tooltipRadius
      );
      ctx.lineTo(tooltipX + tooltipWidth, tooltipY + tooltipContentHeight - tooltipRadius);
      ctx.quadraticCurveTo(
        tooltipX + tooltipWidth,
        tooltipY + tooltipContentHeight,
        tooltipX + tooltipWidth - tooltipRadius,
        tooltipY + tooltipContentHeight
      );
      ctx.lineTo(tooltipX + tooltipRadius, tooltipY + tooltipContentHeight);
      ctx.quadraticCurveTo(
        tooltipX,
        tooltipY + tooltipContentHeight,
        tooltipX,
        tooltipY + tooltipContentHeight - tooltipRadius
      );
      ctx.lineTo(tooltipX, tooltipY + tooltipRadius);
      ctx.quadraticCurveTo(tooltipX, tooltipY, tooltipX + tooltipRadius, tooltipY);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = version === 'v1' ? 'black' : 'white';
      ctx.font = '13px Roboto, Arial';
      ctx.textAlign = 'left'; // Align text at the start
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'transparent'; // Remove text shadow

      const textHeight = lines.length * lineHeight;
      const textY = tooltipY + tooltipPadding + tooltipMarginTop + textHeight / 2;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineY = textY - textHeight / 2 + i * lineHeight;
        ctx.fillText(line, tooltipX + tooltipPadding, lineY); // Adjust the x-coordinate to include the tooltip padding
      }

      ctx.restore();
    },

    clearTooltip(chart) {
      const { ctx } = chart;
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      chart.render();
    },
  };
};

export function calculateDifference(date1, date2) {
  const oneMonthInMilliseconds = 30 * 24 * 60 * 60 * 1000;
  const difference = Math.abs(date1 - date2);
  return difference > oneMonthInMilliseconds;
}

export function getMenuItems(startData, endDate) {
  const oneMonthInMilliseconds = 30 * 24 * 60 * 60 * 1000;
  const difference = Math.abs(endDate - startData);

  const result = [];

  if (difference <= oneMonthInMilliseconds) {
    result[0] = {
      label: '1w',
      value: YEAR_DURATIONS.week,
    };
    result[1] = {
      label: '2w',
      value: YEAR_DURATIONS.fortnite,
    };
    result[2] = {
      label: '1m',
      value: YEAR_DURATIONS.month,
    };
    return result;
  } else if (difference <= 3 * oneMonthInMilliseconds) {
    result[0] = {
      label: '2w',
      value: YEAR_DURATIONS.fortnite,
    };
    result[1] = {
      label: '1m',
      value: YEAR_DURATIONS.month,
    };
    result[2] = {
      label: '2m',
      value: YEAR_DURATIONS.twoMonths,
    };
    result[3] = {
      label: '3m',
      value: YEAR_DURATIONS.threeMonths,
    };
    return result;
  } else if (difference <= 9 * oneMonthInMilliseconds) {
    result[0] = {
      label: '2w',
      value: YEAR_DURATIONS.fortnite,
    };
    result[1] = {
      label: '3m',
      value: YEAR_DURATIONS.threeMonths,
    };
    result[2] = {
      label: '6m',
      value: YEAR_DURATIONS.sixMonths,
    };
    result[3] = {
      label: '9m',
      value: YEAR_DURATIONS.nineMonths,
    };
    return result;
  } else if (difference <= 12 * oneMonthInMilliseconds) {
    result[0] = {
      label: '2w',
      value: YEAR_DURATIONS.fortnite,
    };
    result[1] = {
      label: '3m',
      value: YEAR_DURATIONS.threeMonths,
    };
    result[2] = {
      label: '6m',
      value: YEAR_DURATIONS.sixMonths,
    };
    result[3] = {
      label: '9m',
      value: YEAR_DURATIONS.nineMonths,
    };
    result[4] = {
      label: '1y',
      value: YEAR_DURATIONS.full,
    };
    return result;
  } else if (difference <= 24 * oneMonthInMilliseconds) {
    result[0] = {
      label: '2w',
      value: YEAR_DURATIONS.fortnite,
    };
    result[1] = {
      label: '3m',
      value: YEAR_DURATIONS.threeMonths,
    };
    result[2] = {
      label: '6m',
      value: YEAR_DURATIONS.sixMonths,
    };
    result[4] = {
      label: '1y',
      value: YEAR_DURATIONS.full,
    };
    result[5] = {
      label: '2y',
      value: YEAR_DURATIONS.twoYears,
    };
    return result;
  } else {
    result[0] = {
      label: '2w',
      value: YEAR_DURATIONS.fortnite,
    };
    result[1] = {
      label: '3m',
      value: YEAR_DURATIONS.threeMonths,
    };
    result[2] = {
      label: '6m',
      value: YEAR_DURATIONS.sixMonths,
    };
    result[4] = {
      label: '1y',
      value: YEAR_DURATIONS.full,
    };
    result[5] = {
      label: '2y',
      value: YEAR_DURATIONS.twoYears,
    };
    result[6] = {
      label: '3y',
      value: YEAR_DURATIONS.threeYears,
    };
    return result;
  }
}

export function returnDefaultValues(startData, endDate) {
  const oneMonthInMilliseconds = 30 * 24 * 60 * 60 * 1000;
  const difference = Math.abs(endDate - startData);

  if (difference <= oneMonthInMilliseconds) {
    return YEAR_DURATIONS.week;
  } else if (difference <= 3 * oneMonthInMilliseconds) {
    return YEAR_DURATIONS.month;
  } else if (difference <= 9 * oneMonthInMilliseconds) {
    return YEAR_DURATIONS.quarter;
  } else {
    return YEAR_DURATIONS.half;
  }
}
export const calculateMinDate = duration => {
  const currentDate = dayjs();
  switch (duration) {
    case YEAR_DURATIONS.week:
      return currentDate.subtract(1, 'week');
    case YEAR_DURATIONS.fortnite:
      return currentDate.subtract(2, 'weeks');
    case YEAR_DURATIONS.month:
      return currentDate.subtract(1, 'month');
    case YEAR_DURATIONS.twoMonths:
      return currentDate.subtract(2, 'month');
    case YEAR_DURATIONS.quarter:
      return currentDate.subtract(3, 'months');
    case YEAR_DURATIONS.threeMonths:
      return currentDate.subtract(3, 'months');
    case YEAR_DURATIONS.sixMonths:
      return currentDate.subtract(6, 'months');
    case YEAR_DURATIONS.nineMonths:
      return currentDate.subtract(9, 'months');
    case YEAR_DURATIONS.half:
      return currentDate.subtract(6, 'months');
    case YEAR_DURATIONS.full:
      return currentDate.subtract(1, 'year');
    case YEAR_DURATIONS.twoYears:
      return currentDate.subtract(2, 'year');
    case YEAR_DURATIONS.threeYears:
      return currentDate.subtract(3, 'year');
    default:
      return currentDate;
  }
};

export const showAlert = (severity = 'info', message) => {
  store.dispatch(addAlert({ severity, message }));
};

// Clear all redux state data (persisted and in-memory; non persisted)
export const resetAppState = async ({ preserve = [], reload = false } = {}) => {
  await persistor.purge();
  store.dispatch({ type: 'RESET_APP', preserveKeys: preserve });
  if (reload) window.location.reload();
};

export const fetchAccountSettings = async () => {
  let res = null;
  try {
    res = await apiService.getProfile();
    store.dispatch(setUser(res.data.data));
  }
  catch (error) {
    console.error(error);
  }
};